import { useCallback, useEffect, useRef } from 'react';

import { useVersioning } from '@/data/context/VersioningContext';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import Activity from '@/models/Activity';
import { Question } from '@/models/Question';
import toQuestionIdentityKey from '@/utils/questions/toQuestionIdentityKey';
import { hashMap } from '@/utils/shared/hashMap';

type UseQuestionsSyncProps = {
  activity: Activity;
  cacheManager: ListCacheManager<Activity>;
};

const toKey = (question: Question) =>
  toQuestionIdentityKey(question.type, question.identifier);

const withSaved = (questions: Question[], saved: Question[]) => {
  const byKey = hashMap(questions, toKey);
  for (const question of saved) byKey.set(toKey(question), question);
  return [...byKey.values()].sort((a, b) => a.order - b.order);
};

export default function useQuestionsSync({
  activity,
  cacheManager,
}: UseQuestionsSyncProps) {
  const { getCurrent, setCurrent } = useVersioning<Activity>();
  const savedQuestionsRef = useRef<Question[]>();

  const toCachedActivity = useCallback(
    (questions: Question[]) => {
      const editableActivity = getCurrent();
      if (editableActivity.id === activity.id && !savedQuestionsRef.current)
        return;
      if (!cacheManager.getById(activity.id)) return;
      return { ...editableActivity, id: activity.id, questions };
    },
    [activity.id, cacheManager, getCurrent],
  );

  const commitVersionedId = useCallback(() => {
    const cachedActivity = toCachedActivity(getCurrent().questions);
    if (cachedActivity)
      cacheManager.update(cachedActivity, { newId: getCurrent().id });
  }, [cacheManager, getCurrent, toCachedActivity]);

  const commitVersionedIdRef = useRef(commitVersionedId);
  commitVersionedIdRef.current = commitVersionedId;

  useEffect(() => () => commitVersionedIdRef.current(), []);

  const commitQuestions = useCallback(
    (apply: (questions: Question[]) => Question[]) => {
      const formQuestions = apply(
        savedQuestionsRef.current ?? activity.questions ?? [],
      );
      savedQuestionsRef.current = formQuestions;
      setCurrent({ ...getCurrent(), questions: apply(getCurrent().questions) });
      const cachedActivity = toCachedActivity(formQuestions);
      if (cachedActivity) cacheManager.update(cachedActivity);
    },
    [
      activity.questions,
      cacheManager,
      getCurrent,
      setCurrent,
      toCachedActivity,
    ],
  );

  const syncSaved = useCallback(
    (saved: Question[]) =>
      commitQuestions(questions => withSaved(questions, saved)),
    [commitQuestions],
  );

  const syncDeleted = useCallback(
    (_: Activity, target: Question) =>
      commitQuestions(questions =>
        questions.filter(question => toKey(question) !== toKey(target)),
      ),
    [commitQuestions],
  );

  const syncClose = useCallback(() => {
    commitVersionedId();
    savedQuestionsRef.current = undefined;
  }, [commitVersionedId]);

  return { syncSaved, syncDeleted, syncClose };
}
